import {
  Button,
  createStyles,
  Grid,
  Paper,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import { Show } from "features/shared/components";
import { useConfirmDialog } from "features/shared/hooks/useConfirmDialog";
import React, { useState } from "react";
import { createCorral, deleteCorral, updateCorral } from "redux/operations";
import type { Corral, LivestockInfo, Retreat } from "shared/types";
import { CorralFormState } from "../corral.types";
import { useFilterForm } from "../hooks/useFilterForm";
import CorralFilterForm from "./corralFilterForm";
import CorralForm from "./corralForm";
import CorralListTable from "./corralListTable";

type CorralPageProps = {
  createCorral: (...params: Parameters<typeof createCorral>) => Promise<void>;
  deleteCorral: (...params: Parameters<typeof deleteCorral>) => Promise<void>;
  farmCorrals: Corral[];
  farmRetreats: Retreat[];
  livestockInfosFromCorrals: LivestockInfo[];
  updateCorral: (...params: Parameters<typeof updateCorral>) => Promise<void>;
} & WithStyles<typeof styles>;

function CorralPage({
  classes,
  createCorral,
  deleteCorral,
  farmCorrals,
  farmRetreats,
  livestockInfosFromCorrals,
  updateCorral,
}: CorralPageProps) {
  const { t } = useTranslations();

  const [isFormPanelOpen, setIsFormPanelOpen] = useState<boolean>(false);
  const [selectedCorral, setSelectedCorral] = useState<Corral | null>(null);
  const [targetDeleteCorral, setTargetDeleteCorral] = useState<{
    id: number;
    hasAssociatedLivestock: boolean;
  } | null>(null);

  const { filterForm, handleChangeFilter } = useFilterForm();

  const handleCloseModal = () => {
    setTargetDeleteCorral(null);
  };

  const handleConfirmDelete = () => {
    if (targetDeleteCorral == null) {
      return;
    }

    deleteCorral(targetDeleteCorral.id);
    handleCloseModal();
  };

  const canDeleteCorral = targetDeleteCorral?.hasAssociatedLivestock === false;

  const {
    render: DeleteCorralDialog,
    setVisible,
    setText,
  } = useConfirmDialog({
    cancelButtonText: t("common:actions.cancel"),
    confirmButtonText: t("common:actions.yes"),
    hideButtons: { confirm: !canDeleteCorral },
    onCancel: handleCloseModal,
    onConfirm: handleConfirmDelete,
    title: !canDeleteCorral
      ? t("locales:delete-corral-modal.cannot-delete-title")
      : t("locales:delete-corral-modal.default-title"),
  });

  const handleCloseFormPanel = () => {
    toggleFormPanel(false);
  };

  const toggleFormPanel = (isOpen: boolean) => {
    setIsFormPanelOpen(isOpen);
    if (isOpen) {
      return;
    }
    setSelectedCorral(null);
  };

  const handleOpenNewCorralPanel = () => {
    setSelectedCorral(null);
    toggleFormPanel(true);
  };

  const handleSubmitForm = async (formState: CorralFormState) => {
    if (selectedCorral != null) {
      await updateCorral(selectedCorral.id, {
        name: formState.name.value,
        shortName: formState.shortName.value,
        retreatId: formState.retreatId.value,
      });
      toggleFormPanel(false);
      return;
    }

    await createCorral({
      name: formState.name.value,
      retreatId: formState.retreatId.value,
      shortName: formState.shortName.value,
    });

    toggleFormPanel(false);
  };

  const handleDeleteRequest = (row: Corral) => {
    const corralLivestock = livestockInfosFromCorrals.filter(
      (livestock) => livestock.pastureId === row.id && livestock.endDate == null
    );
    const canDeleteCorral = corralLivestock.length === 0;

    setText(
      !canDeleteCorral
        ? t("locales:delete-corral-modal.cannot-delete-description")
        : t("locales:delete-corral-modal.default-description")
    );
    setTargetDeleteCorral({
      id: row.id,
      hasAssociatedLivestock: !canDeleteCorral,
    });
    setVisible(true);
  };

  const handleEditCorral = (row: Corral) => {
    setSelectedCorral(row);
    toggleFormPanel(true);
  };

  return (
    <>
      <Paper className={classes.root} square={true}>
        <Grid item sm={12} xs={12} className={classes.grid}>
          <Typography className={classes.header} gutterBottom={true}>
            {t("locales:farm-corrals-information")}
          </Typography>
          <CorralFilterForm
            filterForm={filterForm}
            onChangeFilter={handleChangeFilter}
          />

          <Show
            when={isFormPanelOpen}
            fallback={
              <AddNewButton
                classes={classes}
                onClick={handleOpenNewCorralPanel}
              />
            }
          >
            <CorralForm
              corrals={farmCorrals}
              onCancel={handleCloseFormPanel}
              onSubmit={handleSubmitForm}
              retreats={farmRetreats}
              selectedCorral={selectedCorral}
            />
          </Show>

          <CorralListTable
            corrals={farmCorrals}
            filterForm={filterForm}
            isFormPanelOpen={isFormPanelOpen}
            livestockInfos={livestockInfosFromCorrals}
            onDeleteRequest={handleDeleteRequest}
            onEditClick={handleEditCorral}
            retreats={farmRetreats}
          />
        </Grid>
      </Paper>
      <DeleteCorralDialog />
    </>
  );
}

type AddNewButtonProps = {
  onClick: () => void;
} & WithStyles<typeof styles>;

function AddNewButton({ classes, onClick }: AddNewButtonProps) {
  const { t } = useTranslations();

  return (
    <Button
      className={classes.addNewButton}
      color="primary"
      data-testid="add-new-corral-button"
      onClick={onClick}
      variant="contained"
    >
      {t("locales:new-corral")}
    </Button>
  );
}

const styles = () =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      overflowX: "auto",
      padding: "46px",
      zIndex: -1,
      bottom: 0,
      top: "17%",
      minHeight: "calc(100vh - 90px)",
    },
    grid: {
      backgroundColor: "#fff",
      flexGrow: 1,
      height: "100%",
      width: "100%",
    },
    header: {
      fontSize: "20px",
      fontWeight: "bold",
      fontStyle: "medium",
      fontStretch: "medium",
      lineHeight: "medium",
      letterSpacing: "medium",
      color: "#333333",
      marginTop: "40px",
    },
    addNewButton: {
      borderRadius: "18px",
      float: "right",
      marginBottom: "16px",
      marginRight: 0,
      marginTop: "34px",
      width: "153px",
    },
  });

export default withStyles(styles)(CorralPage);
